// @flow

import { action, computed, observable } from "mobx";
import Cookies from "js-cookie";

import Store from "app/stores/Store";
import VideoStore from "app/stores/VideoStore";
import AppearanceStore from "app/stores/AppearanceStore";
import VideoPlayerStore from "app/stores/VideoPlayerStore";
import UserStore from "app/stores/UserStore";

import ContentStatsService from "app/services/ContentStatsService";
import TimelineItemService from "app/services/TimelineItemService";
import TimelineItemCommentService from "app/services/TimelineItemCommentService";
import TimelineItemReactionService from "app/services/TimelineItemReactionService";
import UserService from "app/services/UserService";

import TimelineItemComment from "app/models/TimelineItemComment";
import TimelineItem, {
    MINIMUM_DURATION_SECONDS,
    REACTIONS,
    STATS,
    INTERACTIVE_TYPES
} from "app/models/TimelineItem";
import type { TimelineItemMetadata } from "app/models/TimelineItem";
import TextFormatter from "app/util/TextFormatter";

class TimelineItemStore extends Store {
    @observable items: TimelineItem[] = [];
    @observable selectedItem: ?TimelineItem;
    @observable isExpanded: boolean = false;
    selectedItemBackup: ?TimelineItem;
    @observable wizard: boolean = false;

    wizardChanged: boolean = false;
    @observable waitForCancellationPrompt: boolean = false;

    suppressNextCardCreationMessage = false;

    lastAutoSelectedItem: ?TimelineItem;
    lastItemAutoExpanded: ?TimelineItem;

    @observable canSeekToNextItem: boolean = false;
    @observable canSeekToPreviousItem: boolean = false;

    @observable lessThan5Alert: boolean = false;

    @action
    setItems(items: TimelineItem[]) {
        this.items = items;
    }

    @action
    setLessThan5Alert(value: boolean) {
        this.lessThan5Alert = value;
    }

    /*
     * Update status
     */

    @action
    progressSelect(progress: number, preventAutoPlay: boolean = false): void {
        this.canSeekToPreviousItem = !!this.findPreviousItemByTime(progress);
        this.canSeekToNextItem = !!this.findNextItemByTime(progress);

        if (this.isInSelectedRange(progress)) {
            this.lastAutoSelectedItem = this.selectedItem;
        }

        let item = this.findItemByTime(progress);
        if (item !== this.selectedItem && this.lastAutoSelectedItem !== item) {
            this.select(item, preventAutoPlay);
            this.lastAutoSelectedItem = item;
        }
        if (item
            && (item.stop_time - progress) < 1.0
            && UserStore.user
            && !(AppearanceStore.embed || AppearanceStore.isMobile)
            && item
            && item !== this.lastItemAutoExpanded
            && (item.type === 'audio' || item.isWebcamRecording)) {
                this.lastItemAutoExpanded = item;
            }
    }

    @action.bound
    seekNext(): void {
        let item = this.findNextItemByTime(VideoPlayerStore.progress);
        if (item) {
            this.selectdByUser(item);
        }
    }

    @action.bound
    seekPrevious(): void {
        let item = this.findPreviousItemByTime(VideoPlayerStore.progress);
        if (item) {
            this.selectdByUser(item);
        }
    }

    @action.bound
    select(item: ?TimelineItem, preventAutoPlay: boolean = false): void {
        this.selectedItem = item;
        this.lastItemAutoExpanded = null;
        
        if (item && !VideoStore.isOwner) {
            ContentStatsService.create(item.rowid, STATS.CLICK);
        }

        if (item && INTERACTIVE_TYPES.indexOf(item.type) !== -1) {

            if (VideoStore.shouldAutoPlay && !preventAutoPlay) {
                this.expand();
            }            
        } else {
            this.unExpand();
        }
    }

    @action.bound
    selectdByUser(item: ?TimelineItem) {
        this.select(item);
        if (item) {
            VideoPlayerStore.seekTo(item.start_time);
        }
    }

    @action.bound
    deselect(force): void {
        if (this.wizard) {
            this.cancelWizard();
        }

        if (this.lastAutoSelectedItem !== this.selectedItem || force) {
            this.selectedItem = null;
        }
    }

    @action.bound
    requestEdit(): void {
        const item = this.selectedItem;
        if (!item) return;

        this.startWizard(item.start_time, item);
        this.backupSelectedItem();
    }

    @action.bound
    requestCreate(time): void {
        time = Math.round(time * 100) / 100;

        let creationTime = this.getCreationStartTime(time);
        if (!creationTime && creationTime !== 0) creationTime = time;

        const item = new TimelineItem();
        this.startWizard(creationTime, item);

        item.start_time = creationTime;
        item.stop_time = creationTime + MINIMUM_DURATION_SECONDS;
    }

    @action.bound
    startWizard(time: number, item: TimelineItem): void {
        this.selectedItem = item;

        VideoPlayerStore.pause();
        VideoPlayerStore.seekTo(time);

        this.wizard = true;
        this.wizardChanged = false;
        this.isExpanded = true;
    }

    backupSelectedItem() {
        const item = this.selectedItem;
        if (item) {
            // $FlowFixMe
            this.selectedItemBackup = Object.assign(new TimelineItem(), item);
        }
    }

    @action.bound
    requestWizardCancellation() {
        if (this.wizard && this.wizardChanged) {
            this.waitForCancellationPrompt = true;
        } else {
            this.cancelWizard();
        }
    }

    @action.bound
    resumeWizardCancellation() {
        this.waitForCancellationPrompt = false;
    }

    @action.bound
    cancelWizard(): void {
        this.waitForCancellationPrompt = false;
        this.wizard = false;
        this.isExpanded = false;
        this.restoreSelectedItem();
    }

    @action.bound
    enableWizardChanged(): void {
        this.wizardChanged = true;
    }

    @action.bound
    restoreSelectedItem() {
        if (this.selectedItemBackup) {
            // $FlowFixMe
            Object.assign(this.selectedItem, this.selectedItemBackup);
            this.selectedItemBackup = null;
        } else {
            this.selectedItem = null;
        }
    }

    @action.bound
    drag(time: number): void {
        time = Math.round(time * 100) / 100;

        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");
        time = Math.max(
            0,
            Math.min(VideoStore.video.duration - MINIMUM_DURATION_SECONDS, time)
        );

        if (!this.canMoveTo(time)) {
            if (time < item.start_time) {
                time = this.wizardStartBoundary;
            } else if (time > item.start_time) {
                time = this.wizardStopBoundary - MINIMUM_DURATION_SECONDS;
            }
        }

        if (item.start_time !== time) {
            item.start_time = time;
            item.stop_time = time + MINIMUM_DURATION_SECONDS;

            if (item.start_time !== VideoPlayerStore.progress) {
                VideoPlayerStore.seekTo(item.start_time);
            }
        }
    }

    @action.bound
    updateStartTime(time: number): void {
        time = Math.round(time * 100) / 100;

        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");

        item.start_time = Math.min(
            // @TODO
            item.stop_time - 1,
            Math.max(this.wizardStartBoundary, time)
        );
        VideoPlayerStore.seekTo(item.start_time);
    }

    @action.bound
    updateStopTime(time: number): void {
        time = Math.round(time * 100) / 100;

        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");
        item.stop_time = Math.max(
            // @TODO
            item.start_time + 1,
            Math.min(this.wizardStopBoundary, time)
        );
    }

    @action.bound
    dragStop(): void {
        const item = this.selectedItem;
        if (item.stop_time - item.start_time < MINIMUM_DURATION_SECONDS) {
            this.lessThan5Alert = true;
        }
    }

    @action.bound
    mouseEnter(item: TimelineItem): void {
        if (!VideoStore.isOwner) {
            ContentStatsService.create(item.rowid, STATS.HOVER);
        }
    }

    /*
     * Persist
     */

    @action.bound
    save(
        comment: string,
        url: ?string,
        image: ?string,
        metadata: ?TimelineItemMetadata,
        file?: File
    ): Promise<*> {
        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");

        const newItem = new TimelineItem();
        // $FlowFixMe
        Object.assign(newItem, item, { comment, url, image, metadata });

        const promise = item.rowid
            ? TimelineItemService.update(VideoStore.video, newItem, file)
            : TimelineItemService.create(VideoStore.video, newItem, file);

        return promise.then(
            ({ data }: any) => {
                VideoStore.updateLastSaved();
                // $FlowFixMe
                Object.assign(newItem, TimelineItem.make(data));
                this.onSave(item, newItem);
            },
            ({ response }: any) => {
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    @action
    onSave(oldItem: TimelineItem, newItem: TimelineItem) {
        this.cancelWizard();
        this.select(newItem);
        this.updateItemsArray(oldItem.key, newItem);

        let hasShownSuccessHighlightMessage;
        if (UserStore.user) {
            hasShownSuccessHighlightMessage = UserStore.user.first_card_message;
        } else {
            hasShownSuccessHighlightMessage = Cookies.get("first_card_message");
        }

        if (
            !hasShownSuccessHighlightMessage &&
            !this.suppressNextCardCreationMessage
        ) {
            Cookies.set("first_card_message", true);
            UserService.setFirstCardSuccessMessage();
            AppearanceStore.notifySuccess(
                "Congrats on your first highlight -- keep rockin'!"
            );
        } else {
            this.suppressNextCardCreationMessage = false;
        }
    }

    @action.bound
    remove(): Promise<*> {
        if (!this.selectedItem)
            throw new Error("Trying to remove unselected item");

        const item = this.selectedItem;
        this.deselect(true);

        this.items.splice(this.items.indexOf(item), 1);
        return TimelineItemService.delete(item.rowid).then(() => {
            VideoStore.updateLastSaved();
        });
    }

    @action
    updateItemsArray(key: number, newItem: TimelineItem): void {
        const index = this.items.findIndex(item => item.key === key);
        if (index >= 0) {
            this.items[index] = newItem;
        } else {
            this.items.push(newItem);
        }

        this.items = this.items.sort((a: TimelineItem, b: TimelineItem) =>
            a.start_time > b.start_time ? 1 : -1
        );
    }

    canAddAt(progress: number): boolean {
        if (this.wizard) return false;

        let possibleCreationStartTime = this.getCreationStartTime(progress);
        return possibleCreationStartTime === progress;
    }

    getCreationStartTime(progress: number): ?number {
        const startItem = this.findItemByTime(progress);
        const stopItem = this.findItemByTime(
            progress + MINIMUM_DURATION_SECONDS
        );

        if ((startItem && stopItem) || (!startItem && stopItem)) {
            return;
        } else if (startItem && !stopItem) {
            if (
                !this.findItemByTime(
                    startItem.stop_time + MINIMUM_DURATION_SECONDS
                )
            ) {
                return startItem.stop_time;
            }
            return;
        }

        return progress;
    }

    canMoveTo(progress: number) {
        const startItem = this.findItemByTime(progress);
        if (startItem && startItem !== this.selectedItem) return false;

        const stopItem = this.findItemByTime(
            progress + MINIMUM_DURATION_SECONDS
        );
        if (stopItem && stopItem !== this.selectedItem) return false;

        return true;
    }

    @computed
    get canAdd(): boolean {
        return this.canAddAt(VideoPlayerStore.progress);
    }

    @action.bound
    saveReaction(reaction: number): void {
        const item = this.selectedItem;
        if (!item) return;

        if (this.requireAuthentication()) return;

        const video = VideoStore.video;
        const url =
            window.location.origin +
            "/watch?tlv=" +
            video.uid +
            "&r=" +
            video.version +
            "&st=" +
            item.start_time;
        const time = TextFormatter.seconds(item.start_time);

        if (reaction === REACTIONS.LIKE) {
            this.track("reaction-like", { url, time });
        } else {
            this.track("reaction-dislike", { url, time });
        }

        const user_reaction = item.user_reaction;
        if (reaction === user_reaction) {
            this.deleteReaction();
        } else {
            this.updateReactions(reaction);
        }
    }

    @action
    updateReactions(reaction: number) {
        const item = this.selectedItem;

        if (!item || this.requireAuthentication()) return;

        const userReaction = item.user_reaction || 0;
        if (reaction === userReaction) return;

        item.total_likes = item.total_likes || 0;
        item.total_dislikes = item.total_dislikes || 0;

        if (reaction === REACTIONS.LIKE) {
            item.total_likes++;
            if (userReaction) item.total_dislikes--;
        } else {
            item.total_dislikes++;
            if (userReaction) item.total_likes--;
        }

        item.user_reaction = reaction;
        TimelineItemReactionService.create(item.rowid, reaction);
    }

    @action
    deleteReaction() {
        const item = this.selectedItem;

        if (!item || this.requireAuthentication()) return;

        const userReaction = item.user_reaction;

        item.total_likes = item.total_likes || 0;
        item.total_dislikes = item.total_dislikes || 0;

        if (userReaction === REACTIONS.LIKE) {
            item.total_likes--;
        } else {
            item.total_dislikes--;
        }

        item.user_reaction = 0;
        TimelineItemReactionService.delete(item.rowid);
    }

    @action.bound
    saveComment(message: string): void {
        const selectedItem = this.selectedItem;
        if (!selectedItem) return;

        const user = UserStore.user;
        if (this.requireAuthentication() || !user) return;

        const video = VideoStore.video;
        const url =
            window.location.origin +
            "/watch?tlv=" +
            video.uid +
            "&r=" +
            video.version +
            "&st=" +
            selectedItem.start_time;
        this.track("add-comment", { url, message });

        TimelineItemCommentService.create(selectedItem.rowid, message).then(
            result => {
                // $FlowFixMe
                const comment: TimelineItemComment = TimelineItemComment.make({
                    rowid: result.data.id,
                    author_id: user.rowid,
                    author_profile_id: user.id,
                    author_name: user.displayName,
                    author_image: user.profile_image,
                    author_slug: user.profile_slug,
                    message: message
                });
                this.addComment(comment);
            },
            ({ response }: any) => {
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    @action.bound
    addComment(comment): void {
        if (!this.selectedItem)
            throw Error("selectedItem must not be null/undefined");
        this.selectedItem.comments.unshift(comment);
    }

    @action.bound
    deleteComment(comment: TimelineItemComment): void {
        if (
            comment == null ||
            UserStore.user == null ||
            this.selectedItem == null ||
            comment.author_id !== UserStore.user.rowid
        ) {
            return;
        }

        const comments = this.selectedItem.comments;
        comments.splice(comments.indexOf(comment), 1);

        if (comment.rowid > 0) {
            TimelineItemCommentService.delete(comment.rowid);
        }
    }

    @action.bound
    expand() {
        if(!this.isExpanded) {
            VideoPlayerStore.pause();
        }
        this.isExpanded = true;
    }

    @action.bound
    unExpand() {
        this.isExpanded = false;
        VideoPlayerStore.play();
    }

    @action.bound
    closePictureView() {
        this.isExpanded = false;
        VideoPlayerStore.pause();
    }

    /*
     * Validations
     */
    isInSelectedRange(progress: number): boolean {
        return !!(this.selectedItem && this.selectedItem.inRange(progress));
    }

    findItemByTime(progress: number): ?TimelineItem {
        const items = this.items.slice(0);
        if (this.selectedItem) {
            items.push(this.selectedItem);
        }

        return items
            .reverse()
            .find((item: TimelineItem) => item.inRange(progress));
    }

    findNextItemByTime(progress: number): ?TimelineItem {
        return this.items.find(
            (item: TimelineItem) => item.start_time > progress
        );
    }

    findPreviousItemByTime(progress: number): ?TimelineItem {
        let previous = this.items
            .reverse()
            .find((item: TimelineItem) => item.start_time < progress - 3);

        if (
            !previous &&
            this.items.length > 0 &&
            this.items[0].start_time <= progress
        ) {
            return this.items[0];
        }
        return previous;
    }

    requireAuthentication(): boolean {
        if (UserStore.user) return false;

        let url = `/login?redirect_url=${window.location.href}`;

        const item = this.selectedItem;
        if (item) url += "&st=" + item.start_time;

        window.location.href = url;
        return true;
    }

    /*
     * Calculations
     */

    /**
     * This should be used when dragging and dropping, as the selected item is not on it's correct position
     */
    get orderedItems(): TimelineItem[] {
        let items = this.items;
        if (this.selectedItem && items.indexOf(this.selectedItem) === -1) {
            items = items.concat([this.selectedItem]);
        }

        return items.sort((a: TimelineItem, b: TimelineItem) =>
            a.start_time > b.start_time ? 1 : -1
        );
    }

    @computed
    get wizardStartBoundary(): number {
        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");

        const orderedItems = this.orderedItems;
        const index = orderedItems.indexOf(item);

        if (index === 0) return 0;
        return orderedItems[index - 1].stop_time;
    }

    @computed
    get wizardStopBoundary(): number {
        const item = this.selectedItem;
        if (!item) throw Error("selectedItem must not be null/undefined");

        const orderedItems = this.orderedItems;
        const index = orderedItems.indexOf(item);

        if (index === orderedItems.length - 1) return VideoStore.video.duration;
        return orderedItems[index + 1].start_time;
    }
}

export default new TimelineItemStore();



// WEBPACK FOOTER //
// ./src/app/stores/TimelineItemStore.js